"use client";

import * as HoverCardPrimitive from "@radix-ui/react-hover-card";
import * as React from "react";

import { cn } from "../lib/utils";

function buildQueryString(params: Record<string, string | number | boolean | undefined | null>) {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue;
    sp.append(k, String(v));
  }
  return sp.toString();
}

type PreviewLinkCardContextType = {
  href: string;
  src?: string;
  width?: number;
  height?: number;
  hasImage: boolean;
};

const PreviewLinkCardContext = React.createContext<PreviewLinkCardContextType | undefined>(
  undefined
);

function usePreviewLinkCard() {
  const context = React.useContext(PreviewLinkCardContext);
  if (!context) {
    throw new Error("usePreviewLinkCard must be used within a PreviewLinkCard");
  }
  return context;
}

type PreviewLinkCardProps = React.ComponentProps<typeof HoverCardPrimitive.Root> & {
  href: string;
  src?: string;
  colorScheme?: "light" | "dark";
  deviceScaleFactor?: number;
  width?: number;
  height?: number;
};

function PreviewLinkCard({
  href,
  src,
  width = 240,
  height = 135,
  deviceScaleFactor = 1,
  colorScheme = "light",
  ...props
}: PreviewLinkCardProps) {
  const [hasImage, setHasImage] = React.useState(false);
  const imageSrc =
    src ??
    `https://api.microlink.io/?${buildQueryString({
      url: href,
      screenshot: true,
      meta: false,
      embed: "screenshot.url",
      colorScheme,
      "viewport.isMobile": true,
      "viewport.deviceScaleFactor": deviceScaleFactor,
      "viewport.width": width * 3,
      "viewport.height": height * 3,
    })}`;

  React.useEffect(() => {
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = imageSrc;
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, [imageSrc]);

  React.useEffect(() => {
    setHasImage(false);
    const img = new Image();
    img.onload = () => setHasImage(true);
    img.onerror = () => setHasImage(false);
    img.src = imageSrc;

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [imageSrc]);

  return (
    <PreviewLinkCardContext.Provider value={{ href, src: imageSrc, width, height, hasImage }}>
      <HoverCardPrimitive.Root data-slot="hover-card" {...props} />
    </PreviewLinkCardContext.Provider>
  );
}

type PreviewLinkCardTriggerProps = React.ComponentProps<typeof HoverCardPrimitive.Trigger> &
  React.ComponentProps<"a">;

function PreviewLinkCardTrigger({
  asChild,
  children,
  href: hrefProp,
  ...props
}: PreviewLinkCardTriggerProps) {
  const { href } = usePreviewLinkCard();

  return (
    <HoverCardPrimitive.Trigger data-slot="hover-card-trigger" asChild {...props}>
      {asChild ? children : <a href={hrefProp ?? href}>{children}</a>}
    </HoverCardPrimitive.Trigger>
  );
}

type PreviewLinkCardContentProps = React.ComponentProps<typeof HoverCardPrimitive.Content> &
  React.ComponentProps<"a">;

function PreviewLinkCardContent({
  className,
  align = "center",
  side = "top",
  sideOffset = 4,
  asChild,
  children,
  style,
  href: hrefProp,
  ...props
}: PreviewLinkCardContentProps) {
  const { href, hasImage } = usePreviewLinkCard();

  if (!hasImage) return null;

  return (
    <HoverCardPrimitive.Portal data-slot="hover-card-portal">
      <HoverCardPrimitive.Content
        data-slot="hover-card-content"
        align={align}
        side={side}
        sideOffset={sideOffset}
        className={cn(
          "bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-64 origin-(--radix-hover-card-content-transform-origin) rounded-md border p-0 shadow-md outline-hidden overflow-hidden",
          className
        )}
        asChild={asChild}
        {...(asChild ? { style, ...props } : {})}
        {...props}
      >
        {asChild ? (
          children
        ) : (
          <a
            style={{
              display: "block",
              ...style,
            }}
            href={hrefProp ?? href}
            {...props}
          >
            {children}
          </a>
        )}
      </HoverCardPrimitive.Content>
    </HoverCardPrimitive.Portal>
  );
}

type PreviewLinkCardImageProps = Omit<React.ComponentProps<"img">, "src" | "width" | "height">;

function PreviewLinkCardImage({ alt = "preview image", ...props }: PreviewLinkCardImageProps) {
  const { src, width, height, hasImage } = usePreviewLinkCard();

  if (!hasImage) return null;

  return (
    <img
      className="size-full object-cover"
      src={src}
      width={width}
      height={height}
      alt={alt}
      {...props}
    />
  );
}

export {
  PreviewLinkCard,
  PreviewLinkCardContent,
  PreviewLinkCardImage,
  PreviewLinkCardTrigger,
  usePreviewLinkCard,
};
